import React, { useState, useRef, useEffect } from 'react';

import { input } from '$gbusiness/helpers';
import { INPUT } from '$gbusiness/enums';
import { apiService } from '$gbusiness/services';
import { dialog, FormSection } from '$gcomponents/reusables';
import { Button, HeaderTitle, Text } from '$gcomponents/primitives';
import CategoryModel from '$fbusiness/models/category';
import Footer from '$gcomponents/widgets/footer';
import { PageWrapper } from '$gstyles/wrapper';
import { JPEG_TAG } from '$gbusiness/enums/general';
import intl from '$gintl';

import { CATEGORY_PHOTO_FORM } from '../../inventoryScreen/categories/categoryForm';
import CategoryTreeInput from '../categoryTreeInput';
import { IonContentWrapper } from './styles';
import MULTI_CATEGORY_FORM from './multiCategoryForm';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { categoryActions } from '$fbusiness/redux/category';
import { AppModel } from '$business/redux/';
import FactoryModel from '$fbusiness/models/factory';
import { toNumber } from '$gbusiness/helpers/util';

// The category being edited and everything under it can't be its parent.
const withoutBranch = (list: Array<CategoryModel>, id: number): Array<CategoryModel> =>
  list
    .filter((c) => c.id !== id)
    .map((c) => ({ ...c, children: withoutBranch(c.children || [], id) }));

interface CategoryDetailsProps {
  category: CategoryModel;
  categories: Array<CategoryModel>;
  factory: FactoryModel;
  onClose: Function;
}

const CategoryDetails: React.FC<CategoryDetailsProps> = ({ category, factory, categories, onClose }) => {
  const isFinished = useSelector((state: AppModel) => state.category.isFinished);
  const [checked, setChecked] = useState<Array<number>>(category.parentId ? [category.parentId] : []);
  const dispatch = useDispatch();
  const formRef = useRef<any>();
  const stores = useSelector((state: AppModel) => state.store.stores) || [];
  // Same switch as the item screen's store picker. Loaded per category, and only
  // sent back once loaded, so a save can't clear it by accident.
  const pickStores = !!factory.settings?.storeVisibility && !!category.id && stores.length > 0;
  const [storesLoaded, setStoresLoaded] = useState(false);

  const { taxes } = factory;

  useEffect(() => {
    if (!pickStores) return;
    apiService
      .fetchApi({ url: `categories/${category.id}`, method: 'GET' })
      .then((res) => {
        if (!res?.data?.storeVisibilityIds) return;
        // Set the field directly so edits already typed in the form are kept.
        formRef.current?.setFieldValue('storeVisibilityIds', res.data.storeVisibilityIds, false);
        setStoresLoaded(true);
      })
      .catch(() => {});
  }, [pickStores, category.id]);

  const onSubmit = async (values) => {
    const { image, taxId, ...newValues } = values;

    await dispatch(
      categoryActions.saveCategory(category.id, {
        ...newValues,
        taxId: toNumber(taxId) > 1 ? taxId : null,
        parentId: checked.length ? checked[0] : 0,
        ...(image?.length > 100 && { image: image.replace(JPEG_TAG, '') }),
      }),
    );
    await dispatch(categoryActions.fetchCategories());
  };

  const handleSubmit = async (values) => {
    if (toNumber(values?.taxId) > 0) {
      dialog.confirm({
        message: 'MESSAGE.APPLY_TAX_CATEGORY',
        onPress: async () => {
          await onSubmit(values);
        },
      });
    } else onSubmit(values);
  };

  useEffect(() => {
    if (!isFinished) return;
    dispatch(categoryActions.dehydrate());
    onClose();
  }, [isFinished, onClose, dispatch]);

  const categoryForm = MULTI_CATEGORY_FORM(categories, taxes);
  const validateForm = (values) => {
    return input.validateError(categoryForm, values);
  };

  const handleCategoryInput = (id) => {
    if (checked.includes(id)) setChecked([]);
    else setChecked([id]);
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize={true}
      initialValues={{
        name: category?.name,
        image: category.image?.fileUrl,
        taxId: category.taxId || '0',
      }}
      validate={validateForm}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {(formik) => (
        <>
          <IonContentWrapper>
            <PageWrapper>
              <FormSection formik={formik} FORM={categoryForm} />

              <HeaderTitle>
                <div>{intl('SCREEN.ITEM.PARENT')}</div>
              </HeaderTitle>
              {withoutBranch(categories, category.id).map((cat) => (
                <CategoryTreeInput
                  key={cat.id}
                  level={0}
                  category={cat}
                  checked={checked}
                  onChange={handleCategoryInput}
                />
              ))}
              {storesLoaded && (
                <FormSection
                  title="SCREEN.ITEMS.STORE_VISIBILITY"
                  className="form-section"
                  formik={formik}
                  FORM={[
                    {
                      items: [
                        {
                          label: 'SCREEN.ITEMS.STORE_VISIBILITY_HINT',
                          value: 'storeVisibilityIds',
                          input: INPUT.MULTI_SELECT,
                          disableFastField: true,
                          options: stores.map((s) => ({ labelText: s.name, value: s.id })),
                          position: 'standard',
                          gridSize: 12,
                        },
                      ],
                    },
                  ]}
                />
              )}
              <FormSection
                title="SCREEN.ITEM.CATEGORY_PHOTO"
                className="form-section"
                FORM={CATEGORY_PHOTO_FORM}
                formik={formik}
              />
            </PageWrapper>
          </IonContentWrapper>
          <Footer justifyContent="center">
            <Button className="half" onClick={formik.handleSubmit} disabled={!formik.isValid}>
              <Text k="BUTTON.SUBMIT" />
            </Button>
          </Footer>
        </>
      )}
    </Formik>
  );
};

export default CategoryDetails;
